<template>
  <div class="h-full">
    <TabGroup :selectedIndex="selectedTab" @change="(index)=> selectedTab = index">
      <div class="space-y-4 text-white/80 text-sm h-full">
        <!-- Loading mode -->
        <div v-if="loading" class="flex items-center justify-center h-60">
          <Icon icon="eos-icons:bubble-loading" class="size-12"/>
        </div>

        <!-- Order details form -->
        <TabPanels v-else class="h-full">
          <div class="grid grid-cols-5 gap-6 h-full">
            <div class="col-span-4 flex flex-col gap-4 relative h-full overflow-hidden px-2">
              <div class="flex flex-row gap-2 items-center justify-between">
                <div class="flex flex-row gap-2 items-center">
                  <Tooltip placement="top" arrow>
                    <template #button>
                      <div class="back-btn" @click="backToOrders">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                          <path fill="currentColor"
                                d="m7.825 13l4.9 4.9q.3.3.288.7t-.313.7q-.3.275-.7.288t-.7-.288l-6.6-6.6q-.15-.15-.213-.325T4.426 12t.063-.375t.212-.325l6.6-6.6q.275-.275.688-.275t.712.275q.3.3.3.713t-.3.712L7.825 11H19q.425 0 .713.288T20 12t-.288.713T19 13z"/>
                        </svg>
                      </div>
                    </template>
                    <span>{{ $t('message.back') }}</span>
                  </Tooltip>
                  <TabList class="">
                    <Tab
                        v-slot="{ selected }"
                        as="template"
                        v-for="(item, i) in tabs"
                        :key="i"
                    >
                      <button
                          :class="[
                            selected
                              ? 'text-white bg-primary-700'
                              : 'text-slate-500 bg-white dark:bg-slate-700 dark:text-slate-300',
                          ]"
                          class="text-sm font-medium last:mb-0 capitalize ring-0 foucs:ring-0 focus:outline-none px-6 py-2 transition duration-150 rounded-none first:rounded-l-2xl  last:rounded-r-2xl  "
                      >
                        {{ item.title }}
                      </button>
                    </Tab>
                  </TabList>
                </div>
                <div class="flex flex-row items-center gap-2">
                  <div class="inline-block w-max relative" ref="stateDropdownRef">
                    <button @click="toggleStateDropdown"
                            :class="{ '!border !border-red-300': $vulidate.state.$error }"
                            class="w-full bg-slate-900 border border-slate-700 text-slate-300 rounded-md px-2 py-2 text-left flex justify-between items-center flex-row gap-2">
                      <div class="flex items-center gap-2">
                        <span class="w-2 h-2 rounded-full" :class="stateColorsMap[order.state]"></span>
                        {{ order.state }}
                      </div>
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                      </svg>
                    </button>
                    <div v-if="isStateOpen"
                         class="absolute right-0 z-10 mt-1 w-max min-w-full bg-slate-900 border border-slate-700 text-slate-300 rounded-md shadow-lg">
                      <ul>
                        <li v-for="state in states" :key="state" @click="selectState(state)"
                            class="px-2 py-2 hover:bg-slate-800 cursor-pointer flex items-center gap-2">
                          <span class="w-2 h-2 rounded-full" :class="stateColorsMap[state]"></span>
                          {{ state }}
                        </li>
                      </ul>
                    </div>
                    <template v-if="$vulidate.state.$error">
                      <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.state.required.$response">
                        This field is required
                      </p>
                    </template>
                  </div>
                </div>
              </div>
              <hr class="border-0 h-0.5 bg-slate-800">
              <TabPanel :unmount="false"
                        class="flex-1 flex flex-col gap-6 relative h-full overflow-auto px-2 custom-scrollbar-2">
                <div class="flex flex-row items-start gap-4">
                  <!-- ================== Game ================== -->
                  <div class="flex flex-col gap-0.5 text-sm w-max">
                    <label for="game">Game:</label>
                    <div
                        :class="{ '!border !border-red-300': $vulidate.game.$error }"
                        class="flex flex-row gap-2 items-center w-full bg-slate-900 border border-slate-700 text-slate-300 px-1 rounded-md h-[40px]">
                      <select id="game"
                              v-model="order.game"
                              @change="onGameChange"
                              class="bg-slate-900 h-full focus:ring-0 focus-visible:ring-0 focus-visible:outline-none">
                        <option value="" disabled>-- Select Game --&nbsp</option>
                        <option v-for="g in games" :key="g.game" :value="g.game">{{ g.game }}</option>
                      </select>
                    </div>
                    <template v-if="$vulidate.game.$error">
                      <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.game.required.$response">
                        This field is required
                      </p>
                    </template>
                  </div>

                  <!-- ================== Server ================== -->
                  <div class="flex flex-col gap-0.5 text-sm">
                    <label for="server">Server:</label>
                    <div
                        :class="{ '!border !border-red-300': $vulidate.server.$error }"
                        class="flex flex-row gap-2 items-center w-full bg-slate-900 border border-slate-700 text-slate-300 px-1 rounded-md h-[40px]">
                      <select id="server"
                              v-model="order.server"
                              :disabled="servers.length === 0"
                              class="bg-slate-900 h-full focus:ring-0 focus-visible:ring-0 focus-visible:outline-none">
                        <option value="" disabled>-- Select Server --&nbsp</option>
                        <option v-for="s in servers" :key="s" :value="s">{{ s }}</option>
                      </select>
                    </div>
                    <template v-if="$vulidate.server.$error">
                      <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.server.required.$response">
                        This field is required
                      </p>
                    </template>
                  </div>

                  <!-- ================== Account ================== -->
                  <div class="flex flex-col gap-0.5 text-sm w-max">
                    <label for="account">Account:</label>
                    <div
                        :class="{ '!border !border-red-300': $vulidate.account.$error }"
                        class="flex flex-row gap-2 items-center w-full bg-slate-900 border border-slate-700 text-slate-300 px-1 rounded-md h-[40px]">
                      <select id="account"
                              v-model="order.account"
                              :disabled="accounts.length === 0"
                              class="bg-slate-900 h-full focus:ring-0 focus-visible:ring-0 focus-visible:outline-none w-full">
                        <option value="" disabled class="disabled:bg-slate-900 disabled:hover:bg-slate-900">--
                          Select
                          Account
                          --
                          &nbsp
                        </option>
                        <option
                            v-for="acc in accounts"
                            :key="acc.id"
                            :value="acc.id"
                        >
                          {{ acc.title }}&nbsp
                        </option>
                      </select>
                    </div>
                    <template v-if="$vulidate.account.$error">
                      <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.account.required.$response">
                        This field is required
                      </p>
                    </template>
                  </div>

                  <!-- ================== Side ================== -->
                  <div class="flex flex-col gap-0.5 text-sm w-max">
                    <label for="account">Side:</label>
                    <div class="inline-block w-max relative" ref="sideDropdownRef">
                      <button @click="toggleSideDropdown"
                              :class="{ '!border !border-red-300': $vulidate.side.$error }"
                              class="w-full bg-slate-900 border border-slate-700 text-slate-300 rounded-md px-2 py-2 text-left flex justify-between items-center flex-row gap-2">
                        <div class="flex items-center gap-2">
                          <span class="w-2 h-2 rounded-full"
                                :class="order.side ? sidesColorsMap[order.side] : 'bg-[#9E9E9E]'"></span>
                          {{ order.side ? order.side : 'Not Selected' }}
                        </div>
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                        </svg>
                      </button>
                      <div v-if="isSideOpen"
                           class="absolute right-0 z-10 mt-1 w-max min-w-full bg-slate-900 border border-slate-700 text-slate-300 rounded-md shadow-lg">
                        <ul>
                          <li v-for="side in sides" :key="side" @click="selectSide(side)"
                              class="px-2 py-2 hover:bg-slate-800 cursor-pointer flex items-center gap-2">
                            <span class="w-2 h-2 rounded-full" :class="sidesColorsMap[side]"></span>
                            {{ side }}
                          </li>
                        </ul>
                      </div>
                      <template v-if="$vulidate.side.$error">
                        <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.side.required.$response">
                          This field is required
                        </p>
                      </template>
                    </div>
                  </div>
                </div>
                <div class="grid grid-cols-12 gap-4">
                  <!-- ================== Amount ================== -->
                  <div class="col-span-3 flex-1 flex flex-col gap-0.5 text-sm">
                    <label for="amount">Amount:</label>
                    <div class="">
                      <input id="amount"
                             type="text"
                             :value="order.amount"
                             @keydown="allowDigitsDot"
                             @input="onAmountInput($event.target.value)"
                             :class="{ '!border !border-red-300': $vulidate.amount.$error }"
                             class="input-control w-full block focus:outline-none h-[40px] rounded-md read-only:!bg-slate-200 read-only:dark:!text-black-300 read-only:!text-black-900 read-only:dark:!bg-slate-800">
                    </div>
                    <template v-if="$vulidate.amount.$error">
                      <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.amount.required.$response">
                        This field is required
                      </p>
                    </template>
                  </div>

                  <!-- ================== Price (USD) ================== -->
                  <div class="col-span-3 flex-1 flex flex-col gap-0.5 text-sm">
                    <label for="price">Price:</label>
                    <div class="">
                      <input id="price"
                             type="text"
                             :value="order.price"
                             @keydown="allowDigitsDot"
                             @input="onPriceInput($event.target.value)"
                             :class="{ '!border !border-red-300': $vulidate.price.$error }"
                             class="input-control w-full block focus:outline-none h-[40px] rounded-md read-only:!bg-slate-200 read-only:dark:!text-black-300 read-only:!text-black-900 read-only:dark:!bg-slate-800">
                    </div>
                    <template v-if="$vulidate.price.$error">
                      <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.price.required.$response">
                        This field is required
                      </p>
                    </template>
                  </div>

                  <!-- ================== Value (USD) ================== -->
                  <div class="col-span-3 flex-1 flex flex-col gap-0.5 text-sm">
                    <label for="value">Value:</label>
                    <div class="">
                      <input type="text" id="value"
                             :value="formatNumber(order.value, 2) + ' $'"
                             disabled
                             class="input-control w-full block focus:outline-none h-[40px] rounded-md read-only:!bg-slate-200 read-only:dark:!text-black-300 read-only:!text-black-900 read-only:dark:!bg-slate-800">
                    </div>
                  </div>

                  <!-- ================== Effective Time (USD) ================== -->
                  <!--                  <div class="col-span-3 flex-1 flex flex-col gap-0.5 text-sm">
                                      <label for="price">Effective Time:</label>
                                      <div class="">
                                        <input id="price"
                                               type="text"
                                               :value="order.effectiveTime"
                                               @keydown="allowDigitsDot"
                                               @input="onPriceInput($event.target.value)"
                                               :class="{ '!border !border-red-300': $vulidate.effectiveTime.$error }"
                                               class="input-control w-full block focus:outline-none h-[40px] rounded-md read-only:!bg-slate-200 read-only:dark:!text-black-300 read-only:!text-black-900 read-only:dark:!bg-slate-800">
                                      </div>
                                      <template v-if="$vulidate.effectiveTime.$error">
                                        <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.effectiveTime.required.$response">
                                          This field is required
                                        </p>
                                      </template>
                                    </div>-->
                  <div class="col-span-3 flex-1 flex flex-col gap-0.5 text-sm">
                    <date-picker clearable modal color="gray" :column="1" locale="en"
                                 :locale-config="{en:{dir:{input:'ltr'}}}"
                                 input-format="D MMM YYYY HH:mm" mode="single" format="t" type="datetime"
                                 :styles="{background: 'rgb(15,23,42)', 'text-color':'#ccc', 'primary-color': '#3f69a3', 'secondary-color': '#779dd3', 'border-color': 'transparent', 'icon-background': 'rgb(15,23,42)', 'clear-background': 'rgb(15,23,42)'}"
                                 display-format="D MMM YYYY HH:mm" class="bg-transparent w-full h-full"
                                 label="Effective Time:" :auto-submit="false"
                                 @clear="resetCreatedFrom"
                                 v-model="order.effectiveTime">
                      <template #icon>
                        <Icon icon="uil:calendar-alt" color="white" class="size-5"/>
                      </template>
                      <template #clear>
                        <Icon icon="si:close-fill"/>
                      </template>
                      <template #right-arrow>
                        <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
                             viewBox="314.6749267578125 199.97494506835938 370.62506103515625 600.050048828125"
                             transform="rotate(180 0 0)" width="10" height="10">
                          <path fill="white"
                                d="M419.9 785.6l251.10000000000002-251c9.600000000000023-9.600000000000023 14.299999999999955-22.100000000000023 14.299999999999955-34.60000000000002s-4.7999999999999545-25.100000000000023-14.299999999999955-34.60000000000002l-251.10000000000002-251.09999999999997c-19.099999999999966-19.100000000000023-50.099999999999966-19.100000000000023-69.29999999999995 0l-21.600000000000023 21.599999999999994c-19.100000000000023 19.099999999999994-19.100000000000023 50.099999999999994 0 69.29999999999998l160.2 160.2c9.600000000000023 9.600000000000023 14.300000000000011 22.100000000000023 14.300000000000011 34.60000000000002s-4.800000000000011 25.100000000000023-14.300000000000011 34.60000000000002l-160.2 160.29999999999995c-19.100000000000023 19.100000000000023-19.100000000000023 50.10000000000002 0 69.30000000000007l21.600000000000023 21.59999999999991c19.19999999999999 19 50.19999999999999 19 69.29999999999995-0.1999999999999318z"></path>
                        </svg>
                      </template>
                      <template #left-arrow>
                        <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
                             viewBox="314.6749267578125 199.97494506835938 370.62506103515625 600.050048828125"
                             transform="rotate(360 0 0)" width="10" height="10">
                          <path fill="white"
                                d="M419.9 785.6l251.10000000000002-251c9.600000000000023-9.600000000000023 14.299999999999955-22.100000000000023 14.299999999999955-34.60000000000002s-4.7999999999999545-25.100000000000023-14.299999999999955-34.60000000000002l-251.10000000000002-251.09999999999997c-19.099999999999966-19.100000000000023-50.099999999999966-19.100000000000023-69.29999999999995 0l-21.600000000000023 21.599999999999994c-19.100000000000023 19.099999999999994-19.100000000000023 50.099999999999994 0 69.29999999999998l160.2 160.2c9.600000000000023 9.600000000000023 14.300000000000011 22.100000000000023 14.300000000000011 34.60000000000002s-4.800000000000011 25.100000000000023-14.300000000000011 34.60000000000002l-160.2 160.29999999999995c-19.100000000000023 19.100000000000023-19.100000000000023 50.10000000000002 0 69.30000000000007l21.600000000000023 21.59999999999991c19.19999999999999 19 50.19999999999999 19 69.29999999999995-0.1999999999999318z"></path>
                        </svg>
                      </template>
                      <template #up-arrow>
                        <div class="w-12 h-12 flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
                               viewBox="314.6749267578125 199.97494506835938 370.62506103515625 600.050048828125"
                               transform="rotate(-90 0 0)">
                            <path fill="white"
                                  d="M419.9 785.6l251.10000000000002-251c9.600000000000023-9.600000000000023 14.299999999999955-22.100000000000023 14.299999999999955-34.60000000000002s-4.7999999999999545-25.100000000000023-14.299999999999955-34.60000000000002l-251.10000000000002-251.09999999999997c-19.099999999999966-19.100000000000023-50.099999999999966-19.100000000000023-69.29999999999995 0l-21.600000000000023 21.599999999999994c-19.100000000000023 19.099999999999994-19.100000000000023 50.099999999999994 0 69.29999999999998l160.2 160.2c9.600000000000023 9.600000000000023 14.300000000000011 22.100000000000023 14.300000000000011 34.60000000000002s-4.800000000000011 25.100000000000023-14.300000000000011 34.60000000000002l-160.2 160.29999999999995c-19.100000000000023 19.100000000000023-19.100000000000023 50.10000000000002 0 69.30000000000007l21.600000000000023 21.59999999999991c19.19999999999999 19 50.19999999999999 19 69.29999999999995-0.1999999999999318z"></path>
                          </svg>
                        </div>
                      </template>
                      <template #down-arrow>
                        <div class="w-12 h-12 flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
                               viewBox="314.6749267578125 199.97494506835938 370.62506103515625 600.050048828125"
                               transform="rotate(90 0 0)">
                            <path fill="white"
                                  d="M419.9 785.6l251.10000000000002-251c9.600000000000023-9.600000000000023 14.299999999999955-22.100000000000023 14.299999999999955-34.60000000000002s-4.7999999999999545-25.100000000000023-14.299999999999955-34.60000000000002l-251.10000000000002-251.09999999999997c-19.099999999999966-19.100000000000023-50.099999999999966-19.100000000000023-69.29999999999995 0l-21.600000000000023 21.599999999999994c-19.100000000000023 19.099999999999994-19.100000000000023 50.099999999999994 0 69.29999999999998l160.2 160.2c9.600000000000023 9.600000000000023 14.300000000000011 22.100000000000023 14.300000000000011 34.60000000000002s-4.800000000000011 25.100000000000023-14.300000000000011 34.60000000000002l-160.2 160.29999999999995c-19.100000000000023 19.100000000000023-19.100000000000023 50.10000000000002 0 69.30000000000007l21.600000000000023 21.59999999999991c19.19999999999999 19 50.19999999999999 19 69.29999999999995-0.1999999999999318z"></path>
                          </svg>
                        </div>
                      </template>
                    </date-picker>
                  </div>
                </div>
                <div class="flex-1 flex flex-col gap-0.5 items-start">
                  <label for="notes">Notes:</label>
                  <div class="w-full h-full">
                    <textarea v-model="order.notes" type="text" id="notes" rows="5"
                              oninput="this.style.height = ''; this.style.height = this.scrollHeight + 3 + 'px';"
                              class="input-control resize-none w-full block h-[calc(100%-5px)] min-h-[40px] focus:outline-none rounded-md read-only:!bg-slate-200 read-only:dark:!text-black-300 read-only:!text-black-900 read-only:dark:!bg-slate-800 p-2">
                    </textarea>
                  </div>
                </div>
              </TabPanel>
              <hr class="border-0 h-0.5 bg-slate-800">
              <div class="flex flex-row gap-4 justify-end items-center">
                <button @click="onSave" class="btn btn-outline-primary py-2" :disabled="saving">
                  <span v-if="saving"
                        class="animate-[spin_2s_linear_infinite] inline-block align-middle ltr:mr-2 rtl:ml-2 shrink-0">
                    <Loading/>
                  </span>
                  Save
                </button>
              </div>
            </div>
            <div class="col-span-1 h-full overflow-auto custom-scrollbar-2 rounded-lg pr-2">
              <div class="bg-slate-700 p-2 flex flex-col gap-10 rounded-lg overflow-hidden">
                <div class="flex flex-col gap-1 h-max">
                  <p>Game: {{ order.game }}</p>
                  <p>Server: {{ order.server }}</p>
                  <p>Account: {{ accountTitle }}</p>
                  <p>Side: {{ order.side }}</p>
                </div>
                <p class="break-all" v-html="formatText(order?.logs)"></p>
              </div>
            </div>
          </div>
        </TabPanels>
      </div>
    </TabGroup>
  </div>
</template>

<script setup>
import {ref, reactive, computed, watch, onMounted, toRaw, onBeforeUnmount} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import {useMainOrdersStore} from '@/stores/mainOrders.js'
import {useConfigStore} from '@/stores/config.js'
import {numeric, required} from '@vuelidate/validators';
import {useVuelidate} from '@vuelidate/core';
import Tooltip from "@/components/Tooltip/index.vue";
import Loading from "@/components/Icon/Loading.vue";
import {Icon} from "@iconify/vue";
import {showMessage} from "@/utils/showMessage.js";
import showAlert from "@/utils/showAlert.js";
import {TabGroup, TabList, Tab, TabPanels, TabPanel} from "@headlessui/vue";
import DatePicker from '@alireza-ab/vue3-persian-datepicker';
import PersianDate from "@alireza-ab/persian-date";

/* tabs */
const tabs = [
  {
    title: "Details",
  }
];
const selectedTab = ref(0)

/* Auxiliary keys allowed for all fields */
const helperKeys = [
  'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
  'ArrowLeft', 'ArrowRight', 'Home', 'End'
]


/* ---------- Initial order values (to retrieve) ---------- */
const initialGame = ref('')
const initialServer = ref('')
const initialAccount = ref('')


/* ---------- Stores ---------- */
const route = useRoute()
const router = useRouter()
const ordersStore = useMainOrdersStore()
const configStore = useConfigStore()

/* ---------- Loading status ---------- */
const loading = ref(true)
const saving = ref(false)

/* ---------- The field that the user last edited. ---------- */
//const activeField = ref(null) // 'price' | 'tomanPrice' | null


/* ---------- Initial version of the order for comparison ---------- */
let initialOrder = {}   // شیء ساده (غیر‑ reactive)


/* ---------- Order object ---------- */
const order = reactive({
  state: '',
  game: '',
  server: '',
  account: '',
  side: '',
  amount: '',
  price: '',
  value: '',
  effectiveTime: '',
  notes: '',
  logs: '',
  id: ''
})

const rules = computed(() => {
  return {
    state: {required},
    game: {required},
    server: {required},
    account: {required},
    side: {required},
    amount: {required},
    price: {required},
    value: {required},
    effectiveTime: {required},
  };
});
const $vulidate = useVuelidate(rules, order);

/* ---------- Related to state ---------- */
const isStateOpen = ref(false)
const stateDropdownRef = ref(null)
const states = ['New', 'Delivering', 'Delivered', 'Canceled', 'Paid']
const stateColorsMap = {
  New: 'bg-[#9E9E9E]',
  Delivering: 'bg-[#FF9800]',
  Delivered: 'bg-[#2196F3]',
  Canceled: 'bg-[#F44336]',
  Paid: 'bg-[#4CAF50]',
}
const toggleStateDropdown = () => isStateOpen.value = !isStateOpen.value

const selectState = (state) => {
  order.state = state
  isStateOpen.value = false
}

const handleClickOutside_stateDropdown = (e) => {
  if (stateDropdownRef.value && !stateDropdownRef.value.contains(event.target)) isStateOpen.value = false
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside_stateDropdown)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside_stateDropdown)
})

/* ---------- Related to side ---------- */
const isSideOpen = ref(false)
const sideDropdownRef = ref(null)
const sides = ['Deposit', 'Withdraw']
const sidesColorsMap = {
  Deposit: 'bg-[#4CAF50]',
  Withdraw: 'bg-[#F44336]',
}
const toggleSideDropdown = () => isSideOpen.value = !isSideOpen.value

const selectSide = (state) => {
  order.side = state
  isSideOpen.value = false
}

const handleClickOutside_sideDropdown = (e) => {
  if (sideDropdownRef.value && !sideDropdownRef.value.contains(event.target)) isSideOpen.value = false
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside_sideDropdown)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside_sideDropdown)
})

/* ---------- Select options ---------- */
const games = computed(() => configStore.getGamesByService('StocksManager') || [])

const selectedGame = computed(() =>
    games.value.find(g => g.game === order.game) || null
)

const servers = computed(() =>
    selectedGame.value ? selectedGame.value.servers : []
)
const accounts = computed(() => {
  return order.game ? ordersStore.getGameAccountByGame(order.game) || [] : []
})

/* ---------- Selected account and its title ---------- */
const selectedAccountObj = computed(() =>
    accounts.value.find(a => a.id === order.account) || null
)

const accountTitle = computed(() =>
    selectedAccountObj.value ? selectedAccountObj.value.title : ''
)

function convertTimestamp(timestamp) {
  let p = new PersianDate(Number(timestamp));
  return p.toString('YYYY-MM-DD-HH-mm');
}

/* ---------- Format / Parse ---------- */
const formatText = (text) => {
  return text?.replace(/\n/g, '<br>') || ''
}

/* Digit only (no dots) */
function allowDigits(e) {
  if (
      helperKeys.includes(e.key) ||
      // Allow Ctrl+C / Ctrl+V / Ctrl+A and…:
      (e.ctrlKey || e.metaKey)
  ) return

  if (!/^[0-9]$/.test(e.key)) e.preventDefault()
}

/* Digit + a single dot  */
function allowDigitsDot(e) {
  if (
      helperKeys.includes(e.key) ||
      (e.ctrlKey || e.metaKey)
  ) return

  if (e.key === '.') {
    if (e.target.value.includes('.')) e.preventDefault()
    return
  }

  if (!/^[0-9]$/.test(e.key)) e.preventDefault()
}

/* ---------- Reaction to Game Change ---------- */
function onGameChange() {

  /* ---------- 3‑a) If the same initial Game is selected ---------- */
  if (order.game === initialGame.value) {
    // Server recovery
    if (servers.value.includes(initialServer.value)) {
      order.server = initialServer.value
    } else if (servers.value.length === 1) {
      order.server = servers.value[0]
    } else {
      order.server = ''
    }

    // Account recovery
    if (accounts.value.some(a => a.id === initialAccount.value)) {
      order.account = initialAccount.value
    } else if (accounts.value.length === 1) {
      order.account = accounts.value[0].id
    } else {
      order.account = ''
    }
    return
  }


  order.server =
      servers.value.length === 1 ? servers.value[0] : ''
  order.account =
      accounts.value.length === 1 ? accounts.value[0].id : ''
}

function trimDecimals(num, decimals) {
  return num.toFixed(decimals).replace(/\.?0+$/, '')
}

function formatNumber(val, maxDecimals = 0) {
  if (val === '' || val === null || val === undefined) return ''
  const num = parseFloat(val.toString().replace(/,/g, ''))
  if (isNaN(num)) return ''
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
  })
}


/* ---------- Inputs ---------- */
function onAmountInput(raw) {
  order.amount = sanitizeNumber(raw)
  recalc()
}

function onPriceInput(raw) {
  const cleaned = sanitizeNumber(raw)
  if (cleaned === '') {
    order.price = ''
  } else {
    order.price = cleaned // no trimDecimals — save with all decimals
  }

  /*if (order.price && parseFloat(order.usd_tmn)) {
    order.tomanPrice = (parseFloat(order.price) * parseFloat(order.usd_tmn)).toString()
  } else {
    order.tomanPrice = ''
  }*/

  recalc()
}


/* ---------- Related to effective time input ---------- */
function resetCreatedFrom() {
  order.effectiveTime = null
  //saveCreatedFrom();
}

/*function saveCreatedFrom() {
  emit('updateCreatedFrom', order.effectiveTime)
}*/


/* ---------- Calculations ---------- */
function recalc() {
  const amt = parseFloat(order.amount || 0)
  const prc = parseFloat(order.price || 0)

  // Store the value without decimal places (numeric string)
  order.value = (amt && prc) ? (amt * prc).toString() : ''
}


/*
  * Only digits and a period are allowed if needed.
  * If `maxDecimals` is 0, periods are not allowed.
 */
function sanitizeNumber(raw) {
  let s = raw.replace(/,/g, '')           // Remove commas
  // Let there be only one dot and remove all other non-numeric characters.
  s = s.replace(/[^0-9.]/g, '')

  // Only one point should be kept.
  const parts = s.split('.')
  if (parts.length > 2) {
    s = parts[0] + '.' + parts.slice(1).join('')
  }

  // No limit on the number of digits after the decimal point.
  return s
}


/* ---------- Deactivating prices ---------- */
//const priceDisabled = computed(() => activeField.value === 'tomanPrice')
//const tomans = computed(() => activeField.value === 'price')

/* ---------- Initial fetch ---------- */
async function fetchInitialData() {
  loading.value = true

  // 1) All GameAccounts
  await ordersStore.action_getGameAccounts()

  // 2) Order details
  const data = await ordersStore.action_get_order(
      route.params.orderType,
      route.params.id
  )

  Object.assign(order, data)
  order.effectiveTime = data.effectiveTime ? convertTimestamp(data.effectiveTime) : null

  // Save initial values
  initialGame.value = order.game
  initialServer.value = order.server
  initialAccount.value = order.account

  // 2) Save the original full version to detect changes
  initialOrder = JSON.parse(JSON.stringify(order))

  recalc();
  loading.value = false
}


const showStocksManagerBtn = computed(() =>
    selectedGame.value?.hasStocksManagerService === true
)

/* Object filter: Keep only keys that have the value truthy */
function buildPayload() {
  const payload = {}
  Object.entries(order).forEach(([k, v]) => {
    if (v !== '' && v !== null && v !== undefined) payload[k] = v
  })
  return payload
}

async function onSave() {

  // 1) Validation (if Vuelidate is available)
  $vulidate.value.$touch?.()
  if ($vulidate.value?.$invalid) {
    selectedTab.value = 0
    return false;
  }

  // 3) User verification
  const ok = await showAlert('You are updating order', 'Are you sure?', 'Yes', 'No');
  if (!ok) return

  const keysToSend = ['state', 'game', 'server', 'account', 'side', 'amount', 'price', 'value', 'effectiveTime', 'notes', 'id'];
  const selectedPayload = Object.fromEntries(
      Object.entries(order).filter(([key]) => keysToSend.includes(key))
  );

  // 4) API call
  try {
    saving.value = true
    const res = await ordersStore.action_updateCurrencyTransferOrder(selectedPayload)
    if (res === true) {
      showMessage('Order Updated', 'success');
      backToOrders();
    } else {
      showMessage(res, 'error')
    }
  } catch (err) {
    showMessage(err, 'error')
  } finally {
    saving.value = false
  }
}

function backToOrders() {
  router.push({name: 'stocks-orders'})
}

function onSendToStocksManager() {
  // Here is the logic for calling the API or any action that needs to be taken.
  //console.log('Send to Stocks Manager:', order.id)
}

onMounted(fetchInitialData)

/* ---------- Watch for Game Change ---------- */
/*watch(
    () => order.game,
    () => onGameChange()
)*/
</script>

<style lang="scss">
.input {
  @apply w-full px-3 py-2 border border-gray-700 rounded-md focus:outline-none focus:ring focus:border-blue-500;
}

.back-btn {
  @apply h-8 w-8 bg-slate-200 dark:bg-slate-700 dark:text-slate-200 text-slate-600 flex flex-col justify-center items-center text-base rounded-full cursor-pointer;
}

.pdp-label {
  @apply text-sm;
  margin-bottom: 0 !important;
}

.pdp-icon {
  padding: 0 !important;
}

.pdp-clear {
  position: static !important;
  left: unset !important;
  padding: 0 !important;
}

.pdp {
  @apply flex flex-col h-full gap-0.5;
}

.pdp-group {
  @apply border border-slate-700 rounded-md py-0 px-2 h-[40px];
  display: flex;
  gap: .5rem;
  background-color: rgb(15, 23, 42) !important;
  border-radius: 0.5rem;
  justify-content: space-between;
  align-items: center !important;

  input:focus-visible {
    outline: 0;
  }
}
</style>
